// Nomad-mon Go! — THE DUO GALLERY: one GIANT lenticular Duo at a time, chevrons + a thumbnail rail to
// move between Duos, a drag across the card (or the device tilt) drives the lens only. Shared by the
// Codex (5.1, the lenticular exhibit) and the Forge (the Duos tab's hero). No renderer of its own:
// the lens, the layers and the tilt are forged-card.js's, passed in (o.lens), so this file has no
// imports and stays in whichever chunk uses it. One lens at a time (duoLensFollow is a singleton).
//
// mountDuoGallery(host, o) -> { show(k), follow(), release(), destroy(), current() }
//  o.ids          Duo ids to show (in order)
//  o.card(id)     the card record; o.srcsOf(id) -> its two source cards
//  o.artSrc(d, size)
//  o.lens         { tilt, sweep, follow, release, layers, wire, TILT_SVG, DRAG_SVG } (forged-card.js)
//  o.tilt         { live(), ask } or null: the app's gyro (ask = the iOS permission invite, or null)
//  o.info(d, srcs, i) -> html for the caption beside / under the card (the caller's words)
//  o.onTurn()     the player turned the lens past half (a drag or a real tilt)
//  o.when()       an extra guard for the live tilt (e.g. "my screen is showing")
//  o.sound(name)  optional ('whoosh' on a move); o.reduced, o.still; o.labels { prev, next, ask }

/** The Duos a gallery may always reveal (curated: no excluded people; codex-check vets them). */
export const SHOWCASE_DUOS = Object.freeze(['duo-long-moonwalk', 'duo-mars-mission', 'duo-hanbok-dynasty', 'duo-inside-and-out', 'duo-runway-gq', 'duo-golden-empire']);

const CSS = `
.dg{display:flex;flex-direction:column;align-items:center;gap:10px;min-width:0}
.dg-duo{position:relative;display:flex;align-items:center;justify-content:center;width:fit-content;margin:0 auto}
.dg-slot{flex:none;position:relative;height:var(--dg-h,min(62svh,640px,calc((100vw - 32px) * 1.397)));aspect-ratio:1.26/1.76;touch-action:pan-y;user-select:none;-webkit-user-select:none}
.dg-art{position:absolute;inset:0;border-radius:18px;box-shadow:0 0 0 2px rgba(255,154,232,.6),0 30px 80px -30px rgba(255,154,232,.5)}
.dg-art>img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:18px}
.dg-art.in-r{animation:dgInR .32s cubic-bezier(.2,1,.3,1)}
.dg-art.in-l{animation:dgInL .32s cubic-bezier(.2,1,.3,1)}
.dg-art .dTilt{left:auto;right:4%;bottom:3%;margin:0;width:min(52px,24%);height:auto;aspect-ratio:1}
@keyframes dgInR{from{opacity:0;transform:translateX(14%)}to{opacity:1;transform:none}}
@keyframes dgInL{from{opacity:0;transform:translateX(-14%)}to{opacity:1;transform:none}}
.dg-nav{position:absolute;z-index:8;top:50%;margin-top:-32px;appearance:none;border:0;width:40px;height:64px;border-radius:14px;background:rgba(6,6,12,.55);cursor:pointer;font-size:26px;color:#fff;box-shadow:inset 0 0 0 1px rgba(255,255,255,.18)}
.dg-nav.prev{left:-6px}
.dg-nav.next{right:-6px}
.dg-side{display:grid;justify-items:center;gap:10px;min-width:0}
.dg-rail{display:flex;gap:6px;justify-content:center;flex-wrap:wrap}
.dg-rt{appearance:none;border:0;padding:0;width:36px;height:50px;border-radius:7px;overflow:hidden;cursor:pointer;opacity:.5;box-shadow:inset 0 0 0 1px rgba(255,255,255,.15);background:#111}
.dg-rt img{width:100%;height:100%;object-fit:cover;display:block}
.dg-rt.on{opacity:1;box-shadow:0 0 0 2px #ff9ae8}
.dg-info{display:grid;justify-items:center;gap:4px;text-align:center;min-width:0;overflow-wrap:anywhere}
@media (prefers-reduced-motion:reduce){.dg-art.in-r,.dg-art.in-l{animation:none}}
`;
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

export function mountDuoGallery(host, o) {
  if (!document.getElementById('duoGalleryCss')) {
    const st = document.createElement('style'); st.id = 'duoGalleryCss'; st.textContent = CSS;
    document.head.appendChild(st);
  }
  const ids = o.ids.filter(id => o.card(id) && o.srcsOf(id));
  if (!ids.length) { host.innerHTML = ''; return null; }
  const L = o.lens, lab = o.labels || {}, still = !!o.still;
  const timers = new Set();
  const later = (fn, ms) => { const t = setTimeout(() => { timers.delete(t); fn(); }, ms); timers.add(t); return t; };
  let i = 0, art = null, srcs = null, sweep = null, g = null, iv = 0, turned = false, dead = false;
  const seenSweep = new Set();
  host.innerHTML = `<div class="dg"><div class="dg-duo"><div class="dg-slot"></div><button type="button" class="dg-nav prev" data-d="-1" aria-label="${esc(lab.prev || 'Previous Duo')}">‹</button>
      <button type="button" class="dg-nav next" data-d="1" aria-label="${esc(lab.next || 'Next Duo')}">›</button></div>
    <div class="dg-side">${ids.length > 1 ? `<div class="dg-rail">${ids.map((id, k) => `<button type="button" class="dg-rt" data-k="${k}" aria-label="${esc(o.card(id).name)}"><img alt="" loading="lazy" src="${esc(o.artSrc(o.card(id), 'thumb'))}"></button>`).join('')}</div>` : ''}
    <div class="dg-info" aria-live="polite"></div></div></div>`;
  if (ids.length < 2) for (const b of host.querySelectorAll('.dg-nav')) b.hidden = true;
  const slot = host.querySelector('.dg-slot');
  const guard = () => !dead && !g && !sweep && (!o.when || o.when());
  const follow = () => { if (art && o.tilt && o.tilt.live() && guard()) L.follow(art, srcs, { when: guard, still }); };
  const release = () => { if (sweep) { sweep.stop(true); sweep = null; } L.follow(null); if (art) L.release(art); };
  const turn = () => { if (!turned) { turned = true; if (o.onTurn) o.onTurn(); } };
  const invite = () => {
    if (!art) return;
    if (o.tilt && o.tilt.ask && !o.tilt.live()) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'dTilt'; b.innerHTML = L.TILT_SVG; b.setAttribute('aria-label', lab.ask || 'Turn on tilt');
      b.addEventListener('pointerdown', (e) => e.stopPropagation());
      b.addEventListener('click', async (e) => { e.stopPropagation(); const ok = await o.tilt.ask(); b.remove(); if (ok) follow(); });
      art.appendChild(b);
    } else if (!(o.tilt && o.tilt.live())) {
      const h = document.createElement('span'); h.className = 'dTilt'; h.setAttribute('aria-hidden', 'true'); h.innerHTML = L.DRAG_SVG;
      art.appendChild(h); later(() => h.classList.add('out'), 2400); later(() => h.remove(), 2900);
    }
  };
  const show = (k, dir = 0) => {
    release();
    i = (k + ids.length) % ids.length;
    const d = o.card(ids[i]);
    srcs = o.srcsOf(ids[i]);
    slot.innerHTML = `<span class="fgArt dg-art${dir > 0 ? ' in-r' : dir < 0 ? ' in-l' : ''}" data-duo="${d.id}"><img alt="" decoding="async" src="${esc(o.artSrc(d, 'full'))}">${L.layers(d, { size: 'full', autoplay: true, srcs })}<span class="fgKind">Duo ${L.TILT_SVG}</span></span>`;
    art = slot.querySelector('.dg-art');
    L.wire(slot);
    host.querySelector('.dg-info').innerHTML = o.info ? o.info(d, srcs, i) : `<b>${esc(d.name)}</b>`;
    for (const t of host.querySelectorAll('.dg-rt')) t.classList.toggle('on', +t.dataset.k === i);
    // the invitation: one sweep the first time each Duo shows; then the device tilt follows it
    if (!seenSweep.has(i)) {
      seenSweep.add(i);
      const a0 = art, s0 = srcs;
      later(() => { if (art === a0 && (!o.when || o.when())) { sweep = L.sweep(a0, s0, { dur: 2000, still }); later(() => { sweep = null; follow(); }, 2100); } }, o.reduced ? 120 : 380);
    } else follow();
    invite();
  };
  // a real tilt that turns the lens counts as a turn too (a slow poll, not a frame loop)
  if (o.onTurn) iv = setInterval(() => { if (turned) { clearInterval(iv); return; } if (art && art._lens && art._lens.on && !sweep && !g && Math.abs(art._lens.lb - 0.5) > 0.3) turn(); }, 200);
  slot.addEventListener('pointerdown', (e) => {
    if (!art || e.target.closest('.dTilt')) return;
    g = { id: e.pointerId, x: e.clientX, y: e.clientY, scrub: false };
    try { slot.setPointerCapture(e.pointerId); } catch { /* synthetic */ }
  });
  slot.addEventListener('pointermove', (e) => {
    if (!g || e.pointerId !== g.id) return;
    const dx = e.clientX - g.x, dy = e.clientY - g.y;
    if (!g.scrub && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) { g.scrub = true; if (sweep) { sweep.stop(false); sweep = null; } }
    if (!g.scrub) return;
    const p = Math.max(-1, Math.min(1, dx / (art.getBoundingClientRect().width * 0.45)));
    L.tilt(art, srcs, p, true, { still });
    if (Math.abs(p) > 0.5) turn();
  });
  const up = (e) => { if (!g || (e && e.pointerId !== g.id)) return; g = null; follow(); };
  slot.addEventListener('pointerup', up);
  slot.addEventListener('pointercancel', up);
  host.addEventListener('click', (e) => {
    const nav = e.target.closest('.dg-nav'), rt = e.target.closest('.dg-rt');
    if (!nav && !rt) return;
    const k = rt ? +rt.dataset.k : i + +nav.dataset.d;
    if (k === i) return;
    if (o.sound) o.sound('whoosh');
    show(k, rt ? (k > i ? 1 : -1) : +nav.dataset.d);
  });
  show(0);
  return {
    show, follow, release,
    current: () => ({ i, id: ids[i], art, srcs }),
    destroy() { dead = true; clearInterval(iv); for (const t of timers) clearTimeout(t); timers.clear(); release(); art = null; host.innerHTML = ''; },
  };
}
